import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Quote = Tables<'quotes'> & { organizations: { name: string } | null }
type QuoteLineItem = Tables<'quote_line_items'>

export function AdminQuoteDetailPage() {
  const { id } = useParams()
  const [quote, setQuote] = useState<Quote | null>(null)
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    async function load() {
      const [quoteRes, linesRes] = await Promise.all([
        supabase.from('quotes').select('*, organizations(name)').eq('id', id!).single(),
        supabase.from('quote_line_items').select('*').eq('quote_id', id!).order('line_number'),
      ])
      setQuote(quoteRes.data ? (quoteRes.data as unknown as Quote) : null)
      setLineItems(linesRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading quote...</p>
  if (!quote) return <p>Quote not found</p>

  return (
    <div>
      <PageHeader
        title={`Quote ${quote.reference_code}`}
        description={`${quote.organizations?.name || 'Unknown'} · Version ${quote.version} · Created ${new Date(quote.created_at).toLocaleDateString()}`}
      />

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
        <StatusBadge label={quote.status} />
        {quote.valid_until && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            Valid until {new Date(quote.valid_until).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Line Items */}
      <div className={css.card} style={{ marginBottom: 'var(--space-4)', padding: 0, overflow: 'hidden' }}>
        <table className={css.table}>
          <thead>
            <tr><th>#</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
          </thead>
          <tbody>
            {lineItems.map(item => (
              <tr key={item.id}>
                <td>{item.line_number}</td>
                <td>{item.description || 'Product'}</td>
                <td>{item.quantity}</td>
                <td>£{Number(item.unit_price).toLocaleString()}</td>
                <td style={{ fontWeight: 500 }}>£{Number(item.line_total).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pricing Summary */}
      <div className={css.card} style={{ maxWidth: 400, marginLeft: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--color-text-muted)' }}>Subtotal</span>
            <span>£{Number(quote.subtotal).toLocaleString()}</span>
          </div>
          {Number(quote.discount_amount) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Discount ({quote.discount_pct}%)</span>
              <span>−£{Number(quote.discount_amount).toLocaleString()}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>Total</span>
            <span style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--color-primary-dark)' }}>£{Number(quote.total).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {(quote.customer_notes || quote.internal_notes) && (
        <div className={css.grid2} style={{ marginTop: 'var(--space-6)' }}>
          {quote.customer_notes && (
            <div className={css.card}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>Customer Notes</h3>
              <p style={{ fontSize: 'var(--text-sm)' }}>{quote.customer_notes}</p>
            </div>
          )}
          {quote.internal_notes && (
            <div className={css.card}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>Internal Notes</h3>
              <p style={{ fontSize: 'var(--text-sm)' }}>{quote.internal_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
